/**
 * 메이커 상태 관리 스토어
 * AI 영상 자동 생성 파이프라인 (YouTube 하이라이트, 롱폼, 숏폼) 관리
 */
import { create } from 'zustand'
import type {
  Pipeline,
  ProjectListItem,
  MakerTemplate,
  YouTubeHighlightConfig,
  LongformAutoConfig,
  ShortformAutoConfig,
  WuxiaConfig,
} from '@/types'
import api, { ApiError } from '@/lib/api'

/** 백엔드 메이커 응답 타입 */
interface MakerCreateResponse {
  message: string
  project: { id: string; title: string; type: string; thumbnail?: string | null; createdAt: string; updatedAt: string }
  pipeline: Pipeline
}

interface MakerState {
  /** 현재 활성 파이프라인 */
  currentPipeline: Pipeline | null
  /** 현재 프로젝트 */
  currentProject: ProjectListItem | null
  /** 메이커 템플릿 목록 */
  templates: MakerTemplate[]
  /** 로딩 상태 */
  isLoading: boolean
  /** 에러 메시지 */
  error: string | null
}

interface MakerActions {
  /** YouTube 하이라이트 프로젝트 생성 */
  createYouTubeHighlight: (config: YouTubeHighlightConfig) => Promise<{ projectId: string; pipelineId: string }>
  /** 롱폼 자동 생성 프로젝트 생성 */
  createLongform: (config: LongformAutoConfig) => Promise<{ projectId: string; pipelineId: string }>
  /** 숏폼 자동 생성 프로젝트 생성 */
  createShortform: (config: ShortformAutoConfig) => Promise<{ projectId: string; pipelineId: string }>
  /** 무협 스튜디오 프로젝트 생성 */
  createWuxia: (config: WuxiaConfig) => Promise<{ projectId: string; pipelineId: string }>
  /** 파이프라인 조회 */
  fetchPipeline: (pipelineId: string) => Promise<void>
  /** 파이프라인 재시도 */
  retryPipeline: (pipelineId: string) => Promise<void>
  /** 파이프라인 취소 */
  cancelPipeline: (pipelineId: string) => Promise<void>
  /** 파이프라인 다음 단계 진행 */
  proceedPipeline: (pipelineId: string) => Promise<void>
  /** 파이프라인 단계 데이터 수정 */
  updateStep: (pipelineId: string, stepName: string, data: unknown) => Promise<void>
  /** 메이커 템플릿 목록 조회 */
  fetchTemplates: (type?: string) => Promise<void>
  /** 상태 초기화 */
  reset: () => void
}

type MakerStore = MakerState & MakerActions

const initialState: MakerState = {
  currentPipeline: null,
  currentProject: null,
  templates: [],
  isLoading: false,
  error: null,
}

export const useMakerStore = create<MakerStore>()((set) => ({
  ...initialState,

  // YouTube 하이라이트 생성
  createYouTubeHighlight: async (config: YouTubeHighlightConfig) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.post<MakerCreateResponse>('/makers/youtube-highlight', config)

      set({
        currentPipeline: response.pipeline,
        currentProject: {
          id: response.project.id,
          name: response.project.title,
          thumbnailUrl: response.project.thumbnail ?? undefined,
          duration: 0,
          type: 'youtube_highlight',
          pipelineStatus: response.pipeline.status,
          createdAt: response.project.createdAt,
          updatedAt: response.project.updatedAt,
        },
        isLoading: false,
      })

      return { projectId: response.project.id, pipelineId: response.pipeline.id }
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'YouTube 하이라이트 생성에 실패했습니다.'
      set({ isLoading: false, error: message })
      throw error
    }
  },

  // 롱폼 자동 생성
  createLongform: async (config: LongformAutoConfig) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.post<MakerCreateResponse>('/makers/longform', config)

      set({
        currentPipeline: response.pipeline,
        currentProject: {
          id: response.project.id,
          name: response.project.title,
          thumbnailUrl: response.project.thumbnail ?? undefined,
          duration: 0,
          type: 'longform_auto',
          pipelineStatus: response.pipeline.status,
          createdAt: response.project.createdAt,
          updatedAt: response.project.updatedAt,
        },
        isLoading: false,
      })

      return { projectId: response.project.id, pipelineId: response.pipeline.id }
    } catch (error) {
      const message = error instanceof ApiError ? error.message : '롱폼 자동 생성에 실패했습니다.'
      set({ isLoading: false, error: message })
      throw error
    }
  },

  // 숏폼 자동 생성
  createShortform: async (config: ShortformAutoConfig) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.post<MakerCreateResponse>('/makers/shortform', config)

      set({
        currentPipeline: response.pipeline,
        currentProject: {
          id: response.project.id,
          name: response.project.title,
          thumbnailUrl: response.project.thumbnail ?? undefined,
          duration: 0,
          type: 'shortform_auto',
          pipelineStatus: response.pipeline.status,
          createdAt: response.project.createdAt,
          updatedAt: response.project.updatedAt,
        },
        isLoading: false,
      })

      return { projectId: response.project.id, pipelineId: response.pipeline.id }
    } catch (error) {
      const message = error instanceof ApiError ? error.message : '숏폼 자동 생성에 실패했습니다.'
      set({ isLoading: false, error: message })
      throw error
    }
  },

  // 무협 스튜디오 생성
  createWuxia: async (config: WuxiaConfig) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.post<MakerCreateResponse>('/makers/wuxia', config)

      set({
        currentPipeline: response.pipeline,
        currentProject: {
          id: response.project.id,
          name: response.project.title,
          thumbnailUrl: response.project.thumbnail ?? undefined,
          duration: 0,
          type: 'wuxia_auto',
          pipelineStatus: response.pipeline.status,
          createdAt: response.project.createdAt,
          updatedAt: response.project.updatedAt,
        },
        isLoading: false,
      })

      return { projectId: response.project.id, pipelineId: response.pipeline.id }
    } catch (error) {
      const message = error instanceof ApiError ? error.message : '무협 스튜디오 생성에 실패했습니다.'
      set({ isLoading: false, error: message })
      throw error
    }
  },

  // 파이프라인 조회
  fetchPipeline: async (pipelineId: string) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.get<{ pipeline: Pipeline }>(`/pipelines/${pipelineId}`)
      set({ currentPipeline: response.pipeline, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : '파이프라인 조회에 실패했습니다.'
      set({ isLoading: false, error: message })
    }
  },

  // 파이프라인 재시도
  retryPipeline: async (pipelineId: string) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.post<{ pipeline: Pipeline }>(`/pipelines/${pipelineId}/retry`)
      set({ currentPipeline: response.pipeline, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : '파이프라인 재시도에 실패했습니다.'
      set({ isLoading: false, error: message })
    }
  },

  // 파이프라인 취소
  cancelPipeline: async (pipelineId: string) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.post<{ pipeline: Pipeline }>(`/pipelines/${pipelineId}/cancel`)
      set({ currentPipeline: response.pipeline, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : '파이프라인 취소에 실패했습니다.'
      set({ isLoading: false, error: message })
    }
  },

  // 파이프라인 다음 단계 진행
  proceedPipeline: async (pipelineId: string) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.post<{ pipeline: Pipeline }>(`/pipelines/${pipelineId}/proceed`)
      set({ currentPipeline: response.pipeline, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : '파이프라인 진행에 실패했습니다.'
      set({ isLoading: false, error: message })
    }
  },

  // 파이프라인 단계 데이터 수정
  updateStep: async (pipelineId: string, stepName: string, data: unknown) => {
    set({ isLoading: true, error: null })
    try {
      const response = await api.patch<{ pipeline: Pipeline }>(`/pipelines/${pipelineId}/steps/${stepName}`, { data })
      set({ currentPipeline: response.pipeline, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : '단계 수정에 실패했습니다.'
      set({ isLoading: false, error: message })
    }
  },

  // 메이커 템플릿 목록 조회
  fetchTemplates: async (type?: string) => {
    set({ isLoading: true, error: null })
    try {
      const query = type ? `?type=${encodeURIComponent(type)}` : ''
      const response = await api.get<{ templates: MakerTemplate[] }>(`/makers/templates${query}`)
      set({ templates: response.templates ?? [], isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : '템플릿 목록 조회에 실패했습니다.'
      set({ isLoading: false, error: message })
    }
  },

  // 상태 초기화
  reset: () => set(initialState),
}))
